import { PRODUCT_ENTITLEMENTS } from '@headline/schemas'
import { createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router'
import { AnalyticsView } from '@/components/stream/AnalyticsView'
import { useQuery } from '@tanstack/react-query'
import { accountQuery, subaccountsQuery } from '@/lib/queries'
import { streamConfigQuery, streamHitsQuery } from '@/lib/stream-queries'

/**
 * A client workspace's Analytics — aggregates over the client's own stream. Drill-throughs stay
 * inside the client workspace (its Library), so the URL never leaves the client.
 */
export const Route = createFileRoute(
  '/app/accounts/$accountId_/subaccounts/$subaccountId/analytics',
)({
  loader: async ({ context, params }) => {
    const account = await context.queryClient.ensureQueryData(accountQuery(params.accountId))
    if (!account.entitlements.includes(PRODUCT_ENTITLEMENTS.analytics)) {
      throw redirect({
        to: '/app/accounts/$accountId/subaccounts/$subaccountId',
        params: { accountId: params.accountId, subaccountId: params.subaccountId },
      })
    }
    // Started, not awaited — see the account's Library route (CLI-758).
    void context.queryClient.prefetchQuery(streamHitsQuery(params.subaccountId))
    await context.queryClient.ensureQueryData(streamConfigQuery(params.subaccountId))
  },
  component: ClientAnalyticsPage,
})

function ClientAnalyticsPage() {
  const { accountId, subaccountId } = Route.useParams()
  const navigate = useNavigate()
  const router = useRouter()
  const { data: subs } = useQuery(subaccountsQuery(accountId))
  const client = subs?.subaccounts.find((s) => s.id === subaccountId)

  return (
    <AnalyticsView
      workspaceName={client?.name ?? 'this client'}
      storageKey={subaccountId}
      subaccountId={subaccountId}
      accountId={accountId}
      onDrill={(drill) =>
        void navigate({
          to: '/app/accounts/$accountId/subaccounts/$subaccountId/library',
          params: { accountId, subaccountId },
          search: drill,
        })
      }
      libraryHref={(drill) =>
        router.buildLocation({
          to: '/app/accounts/$accountId/subaccounts/$subaccountId/library',
          params: { accountId, subaccountId },
          search: drill,
        }).href
      }
      onConfigure={() =>
        void navigate({
          to: '/app/accounts/$accountId/subaccounts/$subaccountId/stream-config',
          params: { accountId, subaccountId },
        })
      }
    />
  )
}
